/**
 * S1 (AB#2204, SG-11) — backend codes for staff whose salon access ended, and
 * for staff invitation acceptance (SG-10). One place to read them so the login
 * page, the session-expiry path and the invitation page agree.
 */
import { normalizeApiError } from '../../types/api-errors';

/** Login / refresh / switch-profile refused: the staff record is inactive or deleted. */
export const STAFF_ACCESS_REMOVED = 'AUTH.STAFF_ACCESS_REMOVED';
/** Invitation acceptance: the email or phone already belongs to an account. */
export const STAFF_INVITE_ACCOUNT_EXISTS = 'STAFF_INVITE.ACCOUNT_EXISTS';
/** Invitation token expired, revoked or unknown. */
export const STAFF_INVITE_INVALID = 'STAFF_INVITE.INVALID';

/** i18n key of the "your access was removed" message. */
export const STAFF_ACCESS_REMOVED_KEY = 'stf.login.accessRemoved';

/** The backend error code of a caught error, or null. Tolerates a nested `errors.code[0]`. */
export function apiErrorCode(err: unknown): string | null {
    const appError = normalizeApiError(err);
    if (appError.code && appError.code !== 'UNKNOWN') return appError.code;
    const nested = appError.errors?.code?.[0];
    return typeof nested === 'string' && nested.length > 0 ? nested : null;
}

export function isStaffAccessRemoved(err: unknown): boolean {
    return apiErrorCode(err) === STAFF_ACCESS_REMOVED;
}

/** i18n key for an invitation error, or null when the code is not an invitation code. */
export function staffInviteErrorKey(err: unknown): string | null {
    switch (apiErrorCode(err)) {
        case STAFF_INVITE_ACCOUNT_EXISTS: return 'stf.invite.accountExists';
        case STAFF_INVITE_INVALID: return 'stf.invite.invalid';
        default: return null;
    }
}

/**
 * S1 fix (AB#2204, D2) — ONE "access removed" toast per removal. The same removal
 * can surface from several places at once (the refresh refusal of every in-flight
 * request, the boot check, the profile chooser); whoever claims it first shows the
 * toast, the rest stay quiet for a short window. The login form's inline error box
 * is separate and always shown.
 */
const ANNOUNCE_WINDOW_MS = 10_000;
let lastAnnouncedAt = Number.NEGATIVE_INFINITY;

/** True for the first caller in the window (it should show the toast). */
export function claimAccessRemovedToast(nowMs: number = Date.now()): boolean {
    if (nowMs - lastAnnouncedAt < ANNOUNCE_WINDOW_MS) return false;
    lastAnnouncedAt = nowMs;
    return true;
}

/** Test helper — forget the last announcement. */
export function resetAccessRemovedToastForTests(): void {
    lastAnnouncedAt = Number.NEGATIVE_INFINITY;
}
